import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import Box from '@amzn/meridian/box';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { BoxProps } from '@amzn/meridian/box/box';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import { useAppSelector } from 'src/store/store';
import { setRestrictionListItem } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { getBodyLocationLabel } from 'src/utils/getBodyLocationLabel';
import { isParentTask, RestrictionListItem } from 'src/utils/restrictionUtils';
import { IdentifyBodyLocationEnum, JmrFlowTypes } from 'src/utils/jmrUtils';

/**
 * Select the human-body location for a restriction.
 */
const IdentifyBodyLocationField = (props: { listIndex: number } & BoxProps) => {
  const { listIndex, ...otherProps } = props;
  const dispatch = useDispatch();
  const { restrictionListItems, jmrFlowType, isRestrictionsPageReadOnly } = useAppSelector((state) => state.newJmrPage);
  const task = restrictionListItems[listIndex] as RestrictionListItem;
  const [value, setValue] = useState<IdentifyBodyLocationEnum | null>(task.bodyLocationValue ?? null);

  // Don't wanna reset the max range when preselected restrictionListItem loads,
  // but we do wanna reset the max range when a new task is selected.
  useEffect(() => {
    if (task.bodyLocationValue === undefined && value !== null) {
      setValue(null);
    }
  }, [task.label]);

  // Update the value in the task
  const onChange = (value: IdentifyBodyLocationEnum | null) => {
    setValue(value);
    const taskNew = _.cloneDeep(task);
    if (value === null) {
      delete taskNew.bodyLocationValue;
    } else {
      taskNew.bodyLocationValue = value;
    }
    if (isParentTask(taskNew)) {
      for (const child of taskNew.children) {
        if (value === null) {
          delete child.bodyLocationValue;
        } else {
          child.bodyLocationValue = value;
        }
      }
    }
    dispatch(setRestrictionListItem({ item: taskNew, index: listIndex }));
  };

  // Setup which content to display
  const displaySelection =
    !!task?.identifyBodyLocation || (jmrFlowType === JmrFlowTypes.CompleteTransfer && !!task.bodyLocationValue);
  const displayValueOnly = jmrFlowType === JmrFlowTypes.UpdateDates && !!task.bodyLocationValue;
  if (!displaySelection && !displayValueOnly) {
    return null;
  }

  // "Normal" display state
  return (
    <Box data-testid="new-jmr__identify-body-location-field" {...otherProps}>
      {/* Value Only Display */}
      {displayValueOnly && (
        <Text color="secondary">Restricted body location: {getBodyLocationLabel(task.bodyLocationValue)}</Text>
      )}

      {/* Default Display */}
      {displaySelection && (
        <>
          <Row>
            <Text>
              Identify Body Location{' '}
              <Text tag="span" color="secondary">
                (Optional)
              </Text>
            </Text>
          </Row>
          <ButtonGroup value={value} onChange={(v) => onChange(v === value ? null : v)}>
            <ButtonOption disabled={isRestrictionsPageReadOnly} value={IdentifyBodyLocationEnum.LEFT}>
              Left
            </ButtonOption>
            <ButtonOption disabled={isRestrictionsPageReadOnly} value={IdentifyBodyLocationEnum.RIGHT}>
              Right
            </ButtonOption>
            <ButtonOption disabled={isRestrictionsPageReadOnly} value={IdentifyBodyLocationEnum.BOTH}>
              Both
            </ButtonOption>
          </ButtonGroup>
        </>
      )}
    </Box>
  );
};

export default IdentifyBodyLocationField;
